<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import ConversationSearch from './ConversationSearch.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import ZernioDmWindowBanner from 'dashboard/components-next/Conversation/ZernioDmWindowBanner.vue';
import MetaPaymentIssueBanner from 'dashboard/components-next/Conversation/MetaPaymentIssueBanner.vue';

export default {
  components: {
    ConversationHeader,
    ConversationSearch,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    ZernioDmWindowBanner,
    MetaPaymentIssueBanner,
  },

  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0, showSearch: false };
  },
  computed: {
    // Conversa de comentário (TikTok) ou avaliação (Google): a resposta é pública.
    publicReplyNotice() {
      const kind = this.currentChat?.additional_attributes?.kind;
      if (!this.currentChat?.zernio_public_reply) return '';
      if (kind === 'review') return this.$t('ZERNIO_DM_WINDOW.REVIEW_NOTICE');
      if (kind === 'comment') return this.$t('ZERNIO_DM_WINDOW.COMMENT_NOTICE');
      return '';
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getAppsInConversation',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    // Pendência de pagamento na Meta (131042) da caixa da conversa: a marca
    // vem do store de caixas, não da conversa.
    showMetaPaymentIssue() {
      if (!this.currentChat?.id || !this.currentChat.inbox_id) return false;
      const inbox = this.$store.getters['inboxes/getInbox'](
        this.currentChat.inbox_id
      );
      return Boolean(inbox?.meta_payment_issue);
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
      this.showSearch = false;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-background relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      @toggle-search="showSearch = !showSearch"
    />
    <ConversationSearch
      v-if="showSearch && currentChat.id"
      @close="showSearch = false"
    />
    <MetaPaymentIssueBanner v-if="showMetaPaymentIssue" />
    <ZernioDmWindowBanner
      v-if="currentChat.id && currentChat.zernio_dm_window"
      :window="currentChat.zernio_dm_window"
    />
    <div
      v-else-if="currentChat.id && publicReplyNotice"
      class="px-4 py-2 text-xs border-b bg-n-slate-2 text-n-slate-11 border-n-weak"
    >
      {{ publicReplyNotice }}
    </div>
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="-mt-px border-t border-t-n-background"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
        class="[&_a]:pt-1"
      />
    </woot-tabs>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
